import {AfterViewChecked, Component, OnInit, ViewChild, inject} from '@angular/core';
import {Router} from '@angular/router';
import {UserInfoQuery} from '../../../../common/elf-store/user-info/user-info.query';
import {NotificationsDrawerService} from '../../../../modules/notifications-drawer/notifications-drawer.service';
import {AutoUnsubscribe} from '../../../../decorators/auto-unsubscribe.decorator';
import {AgencyService} from '../../../../services/agency.service';
import {NotificationsService} from '../../../../services/notifications.service';
import {ToastContainerDirective} from 'ngx-toastr';
import {ToastrMessagesProvider} from '../../../../services/toastr-messages.service';
import {UserInfoState} from '../../../../common/elf-store/user-info/user-info.type';
import {ShortUserData, MyAccountSettingsComponent} from './my-account-settings/my-account-settings.component';
import {getFirstLetters} from '../../../../common/utils/helpers';
import {PaginationService} from 'src/app/services/pagination.service';
import {SUBSCRIPTIONS} from 'src/app/common/utils/constants';
import {UserPermissions} from 'src/app/common/elf-store/user-permissions/user-permissions.store';
import {AuthService} from 'src/app/auth/shared/services/auth.service';
import {ExcludedOnlyRegTrackModeComponent} from '../../../../types/only-reg-track-mode.type';
import {filter, switchMap} from 'rxjs/operators';
import {UserPermissionsService} from '../../../../services/user-permissions.service';
import {Title} from '@angular/platform-browser';
import {AmplitudeManager} from '../../../../common/elf-store/amplitude/amplitude.manager';
import {AmplitudeService} from '../../../../services/amplitude.service';
import {TrialExpiredNoCardDialogComponent} from '../../trial-expired-no-card-dialog/trial-expired-no-card-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {SitesQuery} from '../../../../common/elf-store/sites/sites.query';
import {MySitesService} from '../../../../services/my-sites.service';
import {AutofocusDirective} from '../../../../common/directives/mc-autofocus.directive';
import {PermissionGuardDirective} from '../../../../common/directives/permission-guard.directive';
import {TopNotificationsComponent} from '../../top-notifications/top-notifications.component';
import {OnlyRegTrackModeGuardDirective} from '../../../../common/directives/only-reg-track-mode-guard.directive';
import {SearchSiteComponent} from '../../../../modules/search-site/search-site.component';
import {WalkThroughStepDirective} from '../../../../modules/walkthrough/walkthough-step/walkthrough-step.directive';
import {MatIconModule} from '@angular/material/icon';
import {MatToolbarModule} from '@angular/material/toolbar';

@Component({
  standalone: true,
  selector: 'mc-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  imports: [
    AutofocusDirective,
    PermissionGuardDirective,
    ToastContainerDirective,
    TopNotificationsComponent,
    OnlyRegTrackModeGuardDirective,
    SearchSiteComponent,
    WalkThroughStepDirective,
    MyAccountSettingsComponent,
    MatIconModule,
    MatToolbarModule,
  ],
})
@AutoUnsubscribe()
export class HeaderComponent implements OnInit, AfterViewChecked {
  agencyService = inject(AgencyService);
  private router = inject(Router);
  dialog = inject(MatDialog);
  private titleService = inject(Title);
  private authService = inject(AuthService);
  private mySitesService = inject(MySitesService);
  private amplitudeManager = inject(AmplitudeManager);
  private amplitudeService = inject(AmplitudeService);
  private userInfoQuery = inject(UserInfoQuery);
  private pagination = inject(PaginationService);
  private notificationsService = inject(NotificationsService);
  private toastrMessagesProvider = inject(ToastrMessagesProvider);
  private userPermissionsService = inject(UserPermissionsService);
  private notificationsDrawerService = inject(NotificationsDrawerService);

  @ViewChild(ToastContainerDirective) toastContainer: ToastContainerDirective;

  public fullName: string;
  public userShortName: string;
  public email: string;
  public shortUserData: ShortUserData = {
    fullName: '',
    shortName: '',
    email: '',
  };
  homePage: string;
  unreadNotifCounter: number = 0;
  get UserPermissions() {
    return UserPermissions;
  }

  get RegTrackMode() {
    return ExcludedOnlyRegTrackModeComponent;
  }

  get pageTitle() {
    return this.titleService.getTitle();
  }

  ngAfterViewChecked() {
    if (
      this.toastContainer &&
      this.toastrMessagesProvider.mainContainer !== this.toastContainer &&
      !this.toastrMessagesProvider.hasDrawerContainer
    ) {
      this.toastrMessagesProvider.commonMessageContainer = this.toastContainer;
    }
  }

  ngOnInit(): void {
    this[SUBSCRIPTIONS].add(
      this.authService
        .loadUserOrTeamDataBasedOnSwitch()
        .pipe(switchMap(({data}) => this.userPermissionsService.loadUserPermissions(data.user.ssoUserId)))
        .subscribe(),
    );

    this[SUBSCRIPTIONS].add(
      this.userInfoQuery.userInfo$.subscribe((info: UserInfoState) => {
        this.email = info.username;
        this.fullName = `${info.firstName} ${info.lastName}`;
        this.userShortName = getFirstLetters(`${info.firstName} ${info.lastName}`).slice(0, 2);
        this.email = info.username;
        this.homePage = info.homePage;
        this.shortUserData = {
          fullName: this.fullName,
          shortName: this.userShortName,
          email: this.email,
        };
      }),
    );

    this[SUBSCRIPTIONS].add(
      this.mySitesService.isSitesDataFetched$
        .pipe(
          filter((fetched: boolean) => fetched),
          switchMap(() => this.userInfoQuery.userInfo$),
        )
        .subscribe((info: UserInfoState) => {
          this.checkIfTrialExpired(info.trialInfo?.isTrialNoCardExpired);
        }),
    );

    this.notificationsService.unreadNotifCounter.subscribe((count: number) => {
      this.unreadNotifCounter = count;
    });
  }

  checkIfTrialExpired(isTrialNoCardExpired: boolean) {
    const popupShown = sessionStorage.getItem('end-trial-popup-shown');

    if (isTrialNoCardExpired && !popupShown) {
      this.dialog.open(TrialExpiredNoCardDialogComponent);
      sessionStorage.setItem('end-trial-popup-shown', 'true');
    }
  }

  openNotificationsDrawer() {
    this.notificationsDrawerService.open();
  }

  openAddSitesDrawer(e: Event) {
    (e.target as HTMLInputElement).blur();
    void this.router.navigate([`widget/${this.pagination.currentTab}/p/${this.pagination.currentPage}/add-sites`], {
      queryParams: this.pagination.getQueryParams(),
    });

    this.amplitudeManager.setAddSiteProperties({
      addSitesTriggeredPlace: 'header',
      addSiteTriggeredPage: this.amplitudeService.getCurrentPage(),
    });
  }

  navigateToWidgetPage() {
    this.router.navigate([this.homePage || '/widget']);
  }
}
